import { MAX_FILE_DURATION, MAX_FILE_SIZE, STILL_MIME_TYPES, VIDEO_MIME_TYPES } from '../constants'
import { CreationToolSourceFile } from '../types'
import CreationToolError from '../types/error'
import CreationToolOutputType from '../types/output-type'
import getSourceInfo, { getUrlMimeType } from './get-source-info'
import validateImage from './validate-image'
import validateVideo from './validate-video'

const checkFileSize = (source: File | string): Promise<void> => {
    if (typeof source !== 'string') {
        const fileSizeInMB = source.size / (1024 * 1024)
        if (fileSizeInMB > MAX_FILE_SIZE) {
            return Promise.reject(CreationToolError.FileSizeTooLarge)
        }
    }
    return Promise.resolve()
}

type ValidateSourceRequirements = {
    requiresAudio?: boolean
    requiresTransparency?: boolean
}

/**
 * Validates a source file or url based on an array of allowed mime types.
 * This will return a promise that resolves if good, or throws a validation error.
 */
export const validateSourceByInput = async (
    source: File | string,
    allowedMimeTypes: string[],
    requirements?: ValidateSourceRequirements
): Promise<CreationToolSourceFile> => {
    await checkFileSize(source)

    const sourceMimeType = typeof source === 'string' ? getUrlMimeType(source) : source.type
    const allowsVideoOnly = allowedMimeTypes.filter((type) => VIDEO_MIME_TYPES.indexOf(type) < 0).length === 0
    const allowsStillsOnly = allowedMimeTypes.filter((type) => STILL_MIME_TYPES.indexOf(type) < 0).length === 0
    const allowsVideo = !!allowedMimeTypes.find((type) => VIDEO_MIME_TYPES.indexOf(type) > -1)
    const allowsStills = !!allowedMimeTypes.find((type) => STILL_MIME_TYPES.indexOf(type) > -1)
    const { isVideo } = getSourceInfo(source)

    // check if valid file type
    if (!sourceMimeType || allowedMimeTypes.indexOf(sourceMimeType) < 0) {
        if (requirements?.requiresTransparency) {
            return Promise.reject(CreationToolError.NoTransparency)
        }
        if (allowsVideoOnly) {
            return Promise.reject(CreationToolError.InvalidVideoFile)
        }
        if (allowsStillsOnly) {
            return Promise.reject(CreationToolError.InvalidStillFile)
        }
        if (allowsStills && allowsVideo) {
            return Promise.reject(CreationToolError.InvalidMediaFile)
        }
        return Promise.reject(CreationToolError.InvalidAnimatedFile)
    }

    if (isVideo) {
        return validateVideo(source, 0.1, MAX_FILE_DURATION, !!(requirements && requirements.requiresAudio), false)
    }

    return validateImage(source, !allowsStills, !!(requirements && requirements.requiresTransparency))
}

/**
 * Validates a source file or url based on the current output type.
 * This will return a promise that resolves if good, or throws a validation error.
 */
export const validateSourceByOutput = async (
    source: File | string,
    outputType: CreationToolOutputType
): Promise<CreationToolSourceFile> => {
    await checkFileSize(source)

    const { isVideo } = getSourceInfo(source)
    const requiresTransparency = CreationToolOutputType.requiresTransparency(outputType)
    if (!requiresTransparency && (outputType === CreationToolOutputType.Video || isVideo)) {
        const minDuration = CreationToolOutputType.minDuration(outputType)
        const maxDuration = CreationToolOutputType.maxDuration(outputType)
        const requiresAudio = CreationToolOutputType.requiresAudio(outputType)
        return validateVideo(source, minDuration, maxDuration, requiresAudio, false)
    }
    return validateImage(source, true, requiresTransparency)
}
